import { useAppSelector } from 'redux/hooks';
import { Box, Breadcrumbs, Link, LinkProps, Typography } from '@mui/material';
import { useLocation, Link as RouterLink, useParams } from 'react-router-dom';
import { useGetProjectQuery, useGetCalculationQuery } from 'redux/services/projects';
import { headerHeight } from '../Header/Header';
import { styled } from '@mui/material/styles';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { ReactElement } from 'react';

export const breadcrumbHeight = 40;

export const AppBreadcrumbs = (): ReactElement | null => {
  const division = useAppSelector((state) => state.user.division);
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x: string) => x);
  const { calculationId, projectId } = useParams();

  const { data: project } = useGetProjectQuery(projectId || '');
  const { data: calculation } = useGetCalculationQuery({ projectId: projectId!, calculationId: calculationId! });

  const BreadcrumbContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
    display: 'block',
    maxWidth: '1152px',
    [theme.breakpoints.between('xs', 'sm')]: {
      width: '343px',
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      width: '688px',
    },
    [theme.breakpoints.between('lg', 'xl')]: {
      width: '763px',
    },
    [theme.breakpoints.up('xl')]: {
      width: '1152px',
    },
  }));

  const fontStyle = {
    fontSize: 14,
    letterSpacing: 1,
  };

  interface LinkRouterProps extends LinkProps {
    to: string;
    replace?: boolean;
  }

  function LinkRouter(props: LinkRouterProps) {
    return <Link {...props} underline="none" style={fontStyle} component={RouterLink} />;
  }

  if (location.pathname === `/${division}` || location.pathname === '/') {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        backgroundColor: 'white',
        top: headerHeight,
        height: breadcrumbHeight,
        width: '100%',
        marginLeft: 'auto',
        py: 1,
        zIndex: 2,
      }}
    >
      <BreadcrumbContainer>
        <Breadcrumbs aria-label="Breadcrumb" separator={<NavigateNextIcon fontSize="small" style={{ marginBottom: 2 }} />}>
          <LinkRouter color="primary" to={`/${division}`}>
            PROJECTS
          </LinkRouter>
          {pathnames.map((value: string, index: number) => {
            if (value !== 'project' && value !== 'calculation') {
              const last = index === pathnames.length - 1;
              const to = `/${pathnames.slice(0, index + 1).join('/')}`;
              const crumb = to.includes('calculation') ? calculation?.details?.name : project?.name;

              return last ? (
                <Typography color="black" key={to} style={fontStyle}>
                  {crumb?.toUpperCase()}
                </Typography>
              ) : (
                <LinkRouter color="primary" to={to} key={to}>
                  {crumb?.toUpperCase()}
                </LinkRouter>
              );
            }
          })}
        </Breadcrumbs>
      </BreadcrumbContainer>
    </Box>
  );
};
